/**
 * Housekeeping Management - Admin view for task assignment and monitoring
 * Features:
 * - Generate daily tasks
 * - Assign tasks to staff
 * - Monitor progress
 * - View performance metrics
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { 
  RefreshCw, Sparkles, Users, Clock, CheckCircle2, Play, 
  AlertTriangle, Home, Bed, UserPlus, Zap, BarChart3,
  Timer, Loader2, Plus, Filter, Building2
} from 'lucide-react';

// Priority badge colors
const priorityColors = {
  urgent: 'bg-red-500 text-white',
  high: 'bg-orange-100 text-orange-700 border-orange-300',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  low: 'bg-green-100 text-green-700 border-green-300',
};

// Status badge colors
const statusColors = {
  pending: 'bg-gray-100 text-gray-700',
  assigned: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
};

// Task type icons
const TaskTypeIcon = ({ type }) => {
  if (type === 'checkout') return <Home className="h-4 w-4 text-purple-600" />;
  if (type === 'stayover') return <Bed className="h-4 w-4 text-blue-600" />;
  return <Sparkles className="h-4 w-4 text-orange-600" />;
};

export default function HousekeepingManagement({ user, activeHotel }) {
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFloor, setFilterFloor] = useState('all');

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tasksRes, staffRes, summaryRes] = await Promise.all([
        api.get('/housekeeping-tasks').catch(() => ({ data: [] })),
        api.get('/housekeeping-staff').catch(() => ({ data: [] })),
        api.get('/housekeeping-tasks/summary').catch(() => ({ data: null }))
      ]);
      
      setTasks(tasksRes.data || []);
      setStaff(staffRes.data || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load housekeeping data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Generate daily tasks
  const handleGenerateTasks = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/housekeeping-tasks/generate-daily');
      toast.success(`Generated ${res.data.count} tasks`);
      loadData();
    } catch (error) {
      toast.error('Failed to generate tasks');
    } finally {
      setGenerating(false);
    }
  };

  // Handle task selection
  const handleTaskSelect = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  // Select all visible tasks
  const handleSelectAll = () => {
    const visibleTaskIds = filteredTasks.filter(t => t.status === 'pending').map(t => t.id);
    if (selectedTasks.length === visibleTaskIds.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(visibleTaskIds);
    }
  };

  // Assign tasks
  const handleAssign = async () => {
    if (!selectedStaff || selectedTasks.length === 0) return;
    
    setAssigning(true);
    try {
      await api.put('/housekeeping-tasks/bulk-assign', null, {
        params: { task_ids: selectedTasks.join(','), staff_id: selectedStaff }
      });
      toast.success(`Assigned ${selectedTasks.length} tasks`);
      setSelectedTasks([]);
      setAssignDialogOpen(false);
      setSelectedStaff('');
      loadData();
    } catch (error) {
      // Try individual assignment
      try {
        for (const taskId of selectedTasks) {
          await api.put(`/housekeeping-tasks/${taskId}/assign?staff_id=${selectedStaff}`);
        }
        toast.success(`Assigned ${selectedTasks.length} tasks`);
        setSelectedTasks([]);
        setAssignDialogOpen(false);
        setSelectedStaff('');
        loadData();
      } catch (e) {
        toast.error('Failed to assign tasks');
      }
    } finally {
      setAssigning(false);
    }
  };

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    if (filterStatus !== 'all' && task.status !== filterStatus) return false;
    if (filterFloor !== 'all' && String(task.floor) !== filterFloor) return false;
    return true;
  });

  // Get unique floors
  const floors = [...new Set(tasks.map(t => t.floor))].sort((a, b) => a - b);

  // Calculate stats
  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    assigned: tasks.filter(t => t.status === 'assigned').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
  };

  const completionRate = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="housekeeping-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-orange-500" />
            Housekeeping Management
          </h1>
          <p className="text-gray-500 text-sm">Manage cleaning tasks and staff assignments</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={handleGenerateTasks} 
            disabled={generating}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="generate-tasks-btn"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
            ) : (
              <><Zap className="h-4 w-4 mr-2" />Generate Daily Tasks</>
            )}
          </Button>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
            <p className="text-sm text-gray-500">Total Tasks</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
            <p className="text-sm text-yellow-700">Pending</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{stats.assigned}</p>
            <p className="text-sm text-blue-700">Assigned</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">{stats.in_progress}</p>
            <p className="text-sm text-purple-700">In Progress</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
            <p className="text-sm text-green-700">Completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Daily Progress</span>
            <span className="text-sm font-bold text-green-600">{completionRate}%</span>
          </div>
          <Progress value={completionRate} className="h-3" />
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="tasks" className="data-[state=active]:bg-white">
            <Sparkles className="h-4 w-4 mr-2" />
            Tasks ({tasks.length})
          </TabsTrigger>
          <TabsTrigger value="staff" className="data-[state=active]:bg-white">
            <Users className="h-4 w-4 mr-2" />
            Staff ({staff.length})
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-white">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle className="text-lg">Today's Tasks</CardTitle>
                <div className="flex items-center gap-3">
                  {/* Filters */}
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[140px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={filterFloor} onValueChange={setFilterFloor}>
                    <SelectTrigger className="w-[120px]">
                      <Building2 className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Floor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Floors</SelectItem>
                      {floors.map(floor => (
                        <SelectItem key={floor} value={String(floor)}>Floor {floor}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Bulk Assign Button */}
                  {selectedTasks.length > 0 && (
                    <Button 
                      onClick={() => setAssignDialogOpen(true)}
                      className="bg-blue-500 hover:bg-blue-600"
                      data-testid="bulk-assign-btn"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Assign ({selectedTasks.length})
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">
                        <Checkbox 
                          checked={selectedTasks.length > 0 && selectedTasks.length === filteredTasks.filter(t => t.status === 'pending').length}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Room</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Est. Time</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTasks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                          <Sparkles className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                          <p>No tasks found</p>
                          <p className="text-sm">Click "Generate Daily Tasks" to create tasks</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTasks.map(task => (
                        <TableRow key={task.id} data-testid={`task-row-${task.id}`}>
                          <TableCell>
                            {task.status === 'pending' && (
                              <Checkbox 
                                checked={selectedTasks.includes(task.id)}
                                onCheckedChange={() => handleTaskSelect(task.id)}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                                <span className="font-bold text-orange-700">{task.room_number}</span>
                              </div>
                              <div>
                                <p className="font-medium">{task.room_type || 'Standard'}</p>
                                <p className="text-xs text-gray-500">Floor {task.floor}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <TaskTypeIcon type={task.task_type} />
                              <span className="capitalize">{task.task_type}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={priorityColors[task.priority] || priorityColors.medium}>
                              {task.priority}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {task.assigned_to_name ? (
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-gray-400" />
                                <span>{task.assigned_to_name}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Unassigned</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[task.status] || statusColors.pending}>
                              {task.status === 'in_progress' ? (
                                <><Play className="h-3 w-3 mr-1" />In Progress</>
                              ) : task.status === 'completed' ? (
                                <><CheckCircle2 className="h-3 w-3 mr-1" />Done</>
                              ) : task.status === 'assigned' ? (
                                <><Users className="h-3 w-3 mr-1" />Assigned</>
                              ) : (
                                <><Clock className="h-3 w-3 mr-1" />Pending</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Timer className="h-3 w-3" />
                              {task.estimated_time || 30}m
                            </div>
                          </TableCell>
                          <TableCell>
                            {task.status === 'pending' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedTasks([task.id]);
                                  setAssignDialogOpen(true);
                                }}
                              >
                                <UserPlus className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Tab */}
        <TabsContent value="staff" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {staff.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="p-8 text-center text-gray-500">
                  <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p>No housekeeping staff found</p>
                  <p className="text-sm">Add staff with "housekeeping" role in Employee Management</p>
                </CardContent>
              </Card>
            ) : (
              staff.map(member => (
                <Card key={member.id} className="border-gray-200" data-testid={`staff-card-${member.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                          <Users className="h-6 w-6 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{member.name}</p>
                          <p className="text-sm text-gray-500">{member.email}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        Active
                      </Badge>
                    </div>
                    
                    {/* Task Summary */}
                    <div className="grid grid-cols-3 gap-2 text-center bg-gray-50 rounded-lg p-3">
                      <div>
                        <p className="text-xl font-bold text-blue-600">
                          {(member.task_summary?.assigned || 0) + (member.task_summary?.in_progress || 0)}
                        </p>
                        <p className="text-xs text-gray-500">Active</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-green-600">
                          {member.task_summary?.completed || 0}
                        </p>
                        <p className="text-xs text-gray-500">Done</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-gray-600">
                          {member.total_assigned || 0}
                        </p>
                        <p className="text-xs text-gray-500">Total</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* By Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tasks by Status</CardTitle>
              </CardHeader>
              <CardContent>
                {summary?.by_status ? (
                  <div className="space-y-3">
                    {Object.entries(summary.by_status).map(([status, data]) => (
                      <div key={status} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={statusColors[status] || 'bg-gray-100'}>
                            {status}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold">{data.count}</span>
                          {data.avg_duration && (
                            <span className="text-xs text-gray-500 ml-2">
                              avg: {Math.round(data.avg_duration)}m
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* By Type */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tasks by Type</CardTitle>
              </CardHeader>
              <CardContent>
                {summary?.by_type ? (
                  <div className="space-y-3">
                    {Object.entries(summary.by_type).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <TaskTypeIcon type={type} />
                          <span className="capitalize">{type}</span>
                        </div>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* Staff Performance */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Staff Performance (Today)</CardTitle>
              </CardHeader>
              <CardContent>
                {summary?.staff_performance?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Staff</TableHead>
                        <TableHead>Completed</TableHead>
                        <TableHead>On Time</TableHead>
                        <TableHead>Avg Duration</TableHead>
                        <TableHead>Efficiency</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summary.staff_performance.map(perf => {
                        const efficiency = perf.completed > 0 
                          ? Math.round((perf.on_time / perf.completed) * 100) 
                          : 0;
                        return (
                          <TableRow key={perf._id}>
                            <TableCell className="font-medium">{perf.name}</TableCell>
                            <TableCell>{perf.completed}</TableCell>
                            <TableCell>{perf.on_time}</TableCell>
                            <TableCell>
                              {perf.avg_duration ? `${Math.round(perf.avg_duration)}m` : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge className={efficiency >= 80 ? 'bg-green-100 text-green-700' : efficiency >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                                {efficiency}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-gray-500 text-center py-4">No performance data yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-600" />
              Assign Tasks to Staff
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>{selectedTasks.length}</strong> task(s) selected for assignment
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Select Staff Member</label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger data-testid="staff-select">
                  <SelectValue placeholder="Choose a staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name} ({member.total_assigned || 0} tasks)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssign}
              disabled={!selectedStaff || assigning}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-assign-btn"
            >
              {assigning ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Assigning...</>
              ) : (
                <><UserPlus className="h-4 w-4 mr-2" />Assign Tasks</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
